/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import { User } from 'features/auth/types/auth.types';
import { jwtDecode } from 'jwt-decode';
import React, { useMemo, useState } from 'react';
import { ACCESS_TOKEN, CURRENT_USER, REFRESH_TOKEN } from 'utils/constants';
import { isTokenValid } from 'utils/helpers';

export interface AuthContextType {
	login: (
		accessToken: string,
		refreshToken: string,
		callback?: VoidFunction
	) => void;
	logout: (callback?: VoidFunction) => void;
	getAccessToken: () => string;
	getRefreshToken: () => string;
	getRole: () => string;
	setUser: (user: User) => void;
	getUser: () => User | undefined;
}

export const AuthContext = React.createContext<AuthContextType>({
	getUser(): User | undefined {
		return;
	},
	setUser() {},
	login: function (
		_accessToken: string,
		_refreshToken: string,
		_callback?: VoidFunction
	): void {},
	logout: function (_callback?: VoidFunction): void {},
	getAccessToken: function (): string {
		return '';
	},
	getRefreshToken: function (): string {
		return '';
	},
	getRole: function (): string {
		return '';
	}
});

export const AuthProvider = ({
	children
}: {
	children: React.ReactNode;
}): JSX.Element => {
	const [isAuthenticated, setIsAuthenticated] = useState(() => {
		const token = localStorage.getItem(ACCESS_TOKEN);
		return token ? isTokenValid(token) : false;
	});

	const setUser = (user: User): void => {
		localStorage.setItem(CURRENT_USER, JSON.stringify(user));
	};
	const getUser = (): User => {
		const user = localStorage.getItem(CURRENT_USER) ?? '';
		return user && JSON.parse(user);
	};
	const getAccessToken = (): string => {
		const token = localStorage.getItem(ACCESS_TOKEN);
		return token ?? '';
	};
	const getRefreshToken = (): string => {
		const token = localStorage.getItem(REFRESH_TOKEN);
		return token ?? '';
	};
	const getRole = (): string => {
		const role = localStorage.getItem('role');
		return role ?? '';
	};
	const login = (
		accessToken: string,
		refreshToken: string,
		callback?: VoidFunction
	): void => {
		localStorage.setItem(ACCESS_TOKEN, accessToken);
		localStorage.setItem(REFRESH_TOKEN, refreshToken);
		setIsAuthenticated(true);
		callback?.();
	};
	const logout = (callback?: VoidFunction): void => {
		localStorage.clear();
		setIsAuthenticated(false);
		window.location.reload();
		callback?.();
	};

	const value = useMemo(
		() => ({
			getAccessToken,
			getRefreshToken,
			getRole,
			login,
			logout,
			setUser,
			getUser,
			isAuthenticated
		}),
		[
			getAccessToken,
			getRefreshToken,
			getRole,
			login,
			logout,
			setUser,
			getUser,
			isAuthenticated
		]
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
};
